import type { DecoratedProfile } from '@/contexts';
import type { PageExperience } from '@/features/experiences/types';
import { isInteractionAppInherited } from '@/features/interactions/lib/inheritance.lib';
import { BlockTypes, type ExperienceFeed } from '@/types/gql.generated';

// The parent-planner color fields cascaded onto each child view. Passed as a
// narrow slice (not the whole parent) so callers can memoize on these specific
// fields instead of the parent object's identity. NOTE: the Display-section
// toggles (dates-in-primary, highlight-today, shade/mute weekends) are NOT here —
// they are per-view settings that live on each child, not cascaded.
type PlannerColorCascade = Pick<
  PageExperience,
  | 'colorType'
  | 'customHue'
  | 'customSaturation'
  | 'customLightness'
  | 'customAlpha'
  | 'hue'
  | 'saturation'
  | 'lightness'
>;

type DecorateProps = {
  owner: NonNullable<DecoratedProfile>;
  feeds: ExperienceFeed[];
  categories: PageExperience['categories'];
  parentColors: PlannerColorCascade;
};

export const decorateChild = (
  experience: PageExperience,
  { feeds, owner, categories, parentColors }: DecorateProps
): PageExperience => {
  // Color/palette is configured "for all views" on the parent planner (the Style
  // panel writes to the parent), but each child view is fetched as its own
  // experience whose color fields default to an unsaturated grey. Cascade only
  // the color so every child computes --experience-primary-color from the chosen
  // Style-panel color. The Display-section toggles are per-view and stay on the
  // child.
  const {
    colorType,
    customHue,
    customSaturation,
    customLightness,
    customAlpha,
    hue,
    saturation,
    lightness,
  } = parentColors;

  return {
    ...experience,
    colorType,
    customHue,
    customSaturation,
    customLightness,
    customAlpha,
    hue,
    saturation,
    lightness,
    feeds,
    categories,
    owner: {
      id: owner.id,
      avatar: owner.avatar,
      name: owner.email,
    },
  };
};

// The parent-planner interaction configuration cascaded onto each child view.
// Passed as a narrow slice for the same reason as PlannerColorCascade above.
type PlannerInteractionCascade = Pick<
  PageExperience,
  'blocks' | 'interactionApps' | 'interactionSlots'
>;

const findCollectionBlockId = (blocks: PageExperience['blocks']) =>
  blocks.find((block) => block.type === BlockTypes.Collection)?.id;

type InheritOptions = {
  /**
   * Cascade every app, whatever its `isInherited` setting says. What the
   * `interactionInheritEnabled` flag asks for while it is on: the cascade shipped
   * before the setting existed, so the flag keeps it whole until it is flipped
   * off and the per-schedule setting takes over.
   */
  inheritAll?: boolean;
};

/**
 * Cascade the planner's interaction apps onto a child view that has none.
 *
 * The planner owns the interaction apps (its layout declares the per-event slot
 * on its collection block, and the Schedule-settings "Send to calendar" panel
 * writes there), while child views are created with no apps at all — so their
 * event cards resolve an empty interaction list and render no controls.
 *
 * Only the apps whose config says to are passed down ("Include controls in
 * page"), along with the slots that point at them: an author who turns the
 * setting off wants the cards in their page left plain, while the platform bar
 * and the event modal keep offering the interaction (both read the planner
 * itself, not the loaded child, so neither is affected by what happens here).
 *
 * Slots address a block, and every experience owns its own block rows, so the
 * planner's collection-block slots are re-pointed at the CHILD's collection
 * block; left as-is, getExperienceInteractionApps would filter every one of
 * them out. Event-scoped slots are skipped: they are per-event overrides on the
 * planner's own events, and a slot list is applied to every card regardless of
 * its eventId, so copying them would stack duplicate controls on each card.
 *
 * A child that carries its own apps configures its own interactions and is
 * returned untouched.
 */
export const inheritInteractionApps = (
  experience: PageExperience,
  { blocks, interactionApps, interactionSlots }: PlannerInteractionCascade,
  { inheritAll = false }: InheritOptions = {}
): PageExperience => {
  if (experience.interactionApps.length > 0) {
    return experience;
  }

  const inheritedApps = inheritAll
    ? interactionApps
    : interactionApps.filter(isInteractionAppInherited);
  if (inheritedApps.length === 0) {
    return experience;
  }

  const childCollectionBlockId = findCollectionBlockId(experience.blocks);
  const parentCollectionBlockId = findCollectionBlockId(blocks);
  if (!childCollectionBlockId || !parentCollectionBlockId) {
    return experience;
  }

  const inheritedSlots = interactionSlots
    .filter(
      (slot) =>
        slot.blockId === parentCollectionBlockId &&
        slot.eventId == null &&
        inheritedApps.some((app) => app.id === slot.interactionAppId)
    )
    .map((slot) => ({ ...slot, blockId: childCollectionBlockId }));

  if (inheritedSlots.length === 0) {
    return experience;
  }

  return {
    ...experience,
    interactionApps: inheritedApps,
    interactionSlots: inheritedSlots,
  };
};
